import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cv_admin_token";

const CHANNEL_LABELS = { email: "Email", linkedin: "LinkedIn", sms: "SMS" };
const MESSAGE_STATUSES = ["draft", "approved", "sent", "replied", "demo", "won", "declined"];
const CONTACT_STATUSES = ["to-message", "messaged", "replied", "demo", "won", "declined"];

// "Directness" — how candidly the message names and dissolves the
// "is this a sales pitch?" wariness in the reader's head (Voss accusation audit).
const AUDIT_LEVELS = ["OFF", "LIGHT", "MEDIUM", "STRONG"];
const AUDIT_LABELS = { OFF: "Off", LIGHT: "Light", MEDIUM: "Medium", STRONG: "Strong" };
const AUDIT_HINTS = {
  OFF: "No 'this isn't a pitch' line at all.",
  LIGHT: "One soft pre-empt, dissolved in the same breath. (Default)",
  MEDIUM: "One clear, slightly more candid pre-empt.",
  STRONG: "Two short pre-empts woven in.",
};
const AUDIT_DEFAULT = "LIGHT";

const STATUS_COLOURS = {
  draft: "#94a3b8",
  approved: "#a78bfa",
  sent: "#3b82f6",
  replied: "#0ea5e9",
  demo: "#22d3ee",
  won: "#22c55e",
  declined: "#94a3b8",
  "to-message": "#94a3b8",
  messaged: "#3b82f6",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function useAuth() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin/login", { replace: true });
  }, [nav]);
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token || ""}` }), [token]);
  useEffect(() => { if (!token) nav("/admin/login", { replace: true }); }, [token, nav]);
  return { token, authHeader, logout };
}

// shared tab nav between /admin and /admin/outreach
function AdminTabNav({ active }) {
  const nav = useNavigate();
  return (
    <nav className="admin-tabnav" data-testid="admin-tabnav">
      <button
        type="button"
        className={`admin-tabnav-btn ${active === "applications" ? "is-active" : ""}`}
        onClick={() => nav("/admin")}
        data-testid="admin-tabnav-applications"
      >Applications</button>
      <button
        type="button"
        className={`admin-tabnav-btn ${active === "outreach" ? "is-active" : ""}`}
        onClick={() => nav("/admin/outreach")}
        data-testid="admin-tabnav-outreach"
      >Outreach</button>
    </nav>
  );
}

export default function AdminOutreach() {
  const { token, authHeader, logout } = useAuth();
  const [tab, setTab] = useState("people");
  const [industries, setIndustries] = useState([]);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // modals
  const [addOpen, setAddOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [editMessage, setEditMessage] = useState(null);

  const loadIndustries = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/cv-admin/outreach/industries`, { headers: authHeader });
      setIndustries(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [token, authHeader, logout]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/cv-admin/outreach/stats`, { headers: authHeader });
      setStats(data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
  }, [token, authHeader, logout]);

  const loadContacts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cv-admin/outreach/contacts`, { headers: authHeader });
      setContacts(data.items || []);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setError("Couldn't load people.");
    } finally { setLoading(false); }
  }, [token, authHeader, logout]);

  const loadMessages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cv-admin/outreach/messages`, { headers: authHeader });
      setMessages(data.items || []);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setError("Couldn't load messages.");
    } finally { setLoading(false); }
  }, [token, authHeader, logout]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadStats(), loadContacts(), loadMessages()]);
  }, [loadStats, loadContacts, loadMessages]);

  useEffect(() => { loadIndustries(); refreshAll(); }, [loadIndustries, refreshAll]);

  if (!token) return null;

  const industryLabel = (slug) => industries.find((i) => i.slug === slug)?.label || slug;

  // ------- contact actions
  const createPerson = async (payload) => {
    try {
      await axios.post(`${API}/cv-admin/outreach/contacts`, payload, { headers: authHeader });
      setAddOpen(false);
      await Promise.all([loadContacts(), loadStats()]);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't add person.");
    }
  };

  const bulkAdd = async (text, defaultIndustry, defaultChannel) => {
    try {
      const { data } = await axios.post(
        `${API}/cv-admin/outreach/contacts/bulk`,
        { text, default_industry: defaultIndustry, default_channel: defaultChannel },
        { headers: authHeader },
      );
      setPasteOpen(false);
      await Promise.all([loadContacts(), loadStats()]);
      // eslint-disable-next-line no-alert
      alert(`Added ${data.added} people.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't import.");
    }
  };

  const patchContact = async (cid, body) => {
    try {
      await axios.patch(`${API}/cv-admin/outreach/contacts/${cid}`, body, { headers: authHeader });
      await loadContacts();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't update.");
    }
  };

  const deleteContact = async (cid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this person? Their unsent drafts will go too.")) return;
    try {
      await axios.delete(`${API}/cv-admin/outreach/contacts/${cid}`, { headers: authHeader });
      setEditContact(null);
      await refreshAll();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't remove.");
    }
  };

  const draftFor = async (cid, channel, regenerate = false, auditLevel = null) => {
    try {
      const payload = { channel, regenerate };
      if (auditLevel) payload.audit_level = auditLevel;
      const { data } = await axios.post(
        `${API}/cv-admin/outreach/contacts/${cid}/draft`,
        payload,
        { headers: authHeader },
      );
      setEditContact(null);
      setEditMessage(data);
      setTab("messages");
      await Promise.all([loadMessages(), loadStats()]);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't draft a message.");
    }
  };

  // ------- message actions
  const saveMessage = async (mid, body) => {
    try {
      const { data } = await axios.patch(`${API}/cv-admin/outreach/messages/${mid}`, body, { headers: authHeader });
      setEditMessage(data);
      await Promise.all([loadMessages(), loadStats()]);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't save.");
    }
  };

  const approveMessage = async (mid) => {
    try {
      const { data } = await axios.post(`${API}/cv-admin/outreach/messages/${mid}/approve`, {}, { headers: authHeader });
      setEditMessage(data);
      await Promise.all([loadMessages(), loadStats()]);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't approve.");
    }
  };

  const sendEmail = async (mid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Send this email now via Resend?")) return;
    try {
      const { data } = await axios.post(`${API}/cv-admin/outreach/messages/${mid}/send`, {}, { headers: authHeader });
      setEditMessage(data);
      await Promise.all([loadMessages(), loadStats()]);
      // eslint-disable-next-line no-alert
      alert("Sent.");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Send failed.");
    }
  };

  const markSent = async (mid) => {
    try {
      const { data } = await axios.post(`${API}/cv-admin/outreach/messages/${mid}/mark-sent`, {}, { headers: authHeader });
      setEditMessage(data);
      await Promise.all([loadMessages(), loadStats()]);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't mark as sent.");
    }
  };

  const deleteMessage = async (mid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/cv-admin/outreach/messages/${mid}`, { headers: authHeader });
      setEditMessage(null);
      await Promise.all([loadMessages(), loadStats()]);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't delete.");
    }
  };

  const setMessageStatus = async (mid, status) => {
    try {
      const { data } = await axios.patch(`${API}/cv-admin/outreach/messages/${mid}`, { status }, { headers: authHeader });
      setEditMessage((cur) => (cur && cur.id === mid ? data : cur));
      await Promise.all([loadMessages(), loadStats()]);
    } catch {
      // eslint-disable-next-line no-alert
      alert("Couldn't update status.");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      // eslint-disable-next-line no-alert
      alert("Copied.");
    } catch {
      // eslint-disable-next-line no-alert
      alert("Copy failed — select and copy manually.");
    }
  };

  return (
    <div className="admin-shell" data-testid="admin-outreach-page">
      <header className="admin-top">
        <div className="admin-top-inner">
          <div className="logo">
            <span className="wm">Clear<span className="wm-accent">view</span>.</span>
            <span className="admin-top-sub"> · Admin</span>
          </div>
          <div className="admin-top-actions">
            <button type="button" className="admin-link-btn" onClick={refreshAll} data-testid="outreach-refresh">Refresh</button>
            <button type="button" className="admin-link-btn admin-logout" onClick={logout} data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="admin-main">
        <AdminTabNav active="outreach" />

        <IntroBanner stats={stats} />

        <nav className="outreach-subtabs" data-testid="outreach-subtabs">
          <SubTab id="people" active={tab} setActive={setTab} label="People" count={stats?.people_count} />
          <SubTab id="messages" active={tab} setActive={setTab} label="Messages" count={stats?.messages?.by_status?.draft + stats?.messages?.by_status?.approved || 0} />
          <SubTab id="send" active={tab} setActive={setTab} label="Send & track" count={stats?.approved_count} />
        </nav>

        {error && <div className="admin-error">{error}</div>}

        {tab === "people" && (
          <PeopleTab
            contacts={contacts}
            industries={industries}
            industryLabel={industryLabel}
            loading={loading}
            onAdd={() => setAddOpen(true)}
            onPaste={() => setPasteOpen(true)}
            onSelect={(c) => setEditContact(c)}
          />
        )}

        {tab === "messages" && (
          <MessagesTab
            messages={messages}
            industries={industries}
            industryLabel={industryLabel}
            loading={loading}
            onSelect={(m) => setEditMessage(m)}
          />
        )}

        {tab === "send" && (
          <SendTab
            messages={messages}
            stats={stats}
            industryLabel={industryLabel}
            onSendEmail={sendEmail}
            onMarkSent={markSent}
            onCopy={copyToClipboard}
            onStatus={setMessageStatus}
            onSelect={(m) => setEditMessage(m)}
          />
        )}
      </main>

      {addOpen && (
        <AddPersonModal
          industries={industries}
          onCancel={() => setAddOpen(false)}
          onSave={createPerson}
        />
      )}
      {pasteOpen && (
        <PasteModal
          industries={industries}
          onCancel={() => setPasteOpen(false)}
          onSubmit={bulkAdd}
        />
      )}
      {editContact && (
        <ContactDrawer
          contact={editContact}
          industries={industries}
          industryLabel={industryLabel}
          onClose={() => setEditContact(null)}
          onPatch={(b) => patchContact(editContact.id, b)}
          onDelete={() => deleteContact(editContact.id)}
          onDraft={(channel) => draftFor(editContact.id, channel, false)}
          onRegenerate={(channel) => draftFor(editContact.id, channel, true)}
        />
      )}
      {editMessage && (
        <MessageDrawer
          message={editMessage}
          industryLabel={industryLabel}
          onClose={() => setEditMessage(null)}
          onSave={(b) => saveMessage(editMessage.id, b)}
          onApprove={() => approveMessage(editMessage.id)}
          onRegenerate={(channel, auditLevel) => draftFor(editMessage.contact_id, channel, true, auditLevel)}
          onSendEmail={() => sendEmail(editMessage.id)}
          onMarkSent={() => markSent(editMessage.id)}
          onDelete={() => deleteMessage(editMessage.id)}
          onCopy={() => copyToClipboard(editMessage.body)}
          onStatus={(s) => setMessageStatus(editMessage.id, s)}
        />
      )}
    </div>
  );
}

// ===========================================================================
function IntroBanner({ stats }) {
  const people = stats?.people_count ?? 0;
  const approved = stats?.approved_count ?? 0;
  const sent = stats?.sent_count ?? 0;
  return (
    <section className="outreach-intro" data-testid="outreach-banner">
      <div className="outreach-intro-steps">
        <Step n="1" label="Add the people you want to reach" />
        <Step n="2" label="Approve their messages" />
        <Step n="3" label="Send &amp; track replies" />
      </div>
      <div className="outreach-intro-counts" data-testid="outreach-counts">
        <span data-testid="outreach-count-people"><strong>{people}</strong> {people === 1 ? "person" : "people"}</span>
        <span className="outreach-count-dot">·</span>
        <span data-testid="outreach-count-approved"><strong>{approved}</strong> approved</span>
        <span className="outreach-count-dot">·</span>
        <span data-testid="outreach-count-sent"><strong>{sent}</strong> sent</span>
      </div>
    </section>
  );
}

function Step({ n, label }) {
  return (
    <div className="outreach-step">
      <span className="outreach-step-n">{n}</span>
      <span className="outreach-step-label" dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}

function SubTab({ id, active, setActive, label, count }) {
  return (
    <button
      type="button"
      className={`outreach-subtab ${active === id ? "is-active" : ""}`}
      onClick={() => setActive(id)}
      data-testid={`outreach-tab-${id}`}
    >
      <span>{label}</span>
      {typeof count === "number" && count > 0 && <span className="outreach-subtab-count">{count}</span>}
    </button>
  );
}

function StatusBadge({ value }) {
  const v = value || "draft";
  return (
    <span className="outreach-status" style={{ background: STATUS_COLOURS[v] || "#94a3b8" }} data-testid={`outreach-status-${v}`}>
      {v.replace(/-/g, " ")}
    </span>
  );
}

function ChannelBadge({ value }) {
  const v = value || "email";
  return <span className={`outreach-channel outreach-channel-${v}`}>{CHANNEL_LABELS[v] || v}</span>;
}

// ===========================================================================
function PeopleTab({ contacts, industries, industryLabel, loading, onAdd, onPaste, onSelect }) {
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const filtered = contacts.filter((c) => {
    if (industry !== "all" && c.industry !== industry) return false;
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      const blob = `${c.name} ${c.business} ${c.email || ""}`.toLowerCase();
      if (!blob.includes(t)) return false;
    }
    return true;
  });
  return (
    <section data-testid="outreach-people">
      <div className="outreach-toolbar">
        <input
          className="admin-search"
          type="search"
          placeholder="Search people…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="outreach-people-search"
        />
        <select className="admin-select" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="outreach-people-industry">
          <option value="all">All industries</option>
          {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
        </select>
        <div className="outreach-toolbar-spacer" />
        <button type="button" className="outreach-cta-ghost" onClick={onPaste} data-testid="outreach-paste-btn">Paste list / CSV</button>
        <button type="button" className="outreach-cta" onClick={onAdd} data-testid="outreach-add-person-btn">+ Add person</button>
      </div>

      {loading && <div className="outreach-empty">Loading…</div>}

      {!loading && contacts.length === 0 && (
        <div className="outreach-empty" data-testid="outreach-people-empty">
          <p className="outreach-empty-title">No people yet.</p>
          <p className="outreach-empty-body">
            Click <strong>+ Add person</strong> to add someone manually, or <strong>Paste list / CSV</strong> if you've got a few at once.
          </p>
        </div>
      )}

      {!loading && contacts.length > 0 && filtered.length === 0 && (
        <div className="outreach-empty">No people match those filters.</div>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="outreach-list" data-testid="outreach-people-list">
          {filtered.map((c) => (
            <li key={c.id} className="outreach-card" onClick={() => onSelect(c)} data-testid="outreach-person-row">
              <div className="outreach-card-top">
                <div className="outreach-card-name" data-testid="outreach-person-name">{c.name}</div>
                <StatusBadge value={c.status} />
              </div>
              <div className="outreach-card-sub">{c.business} · <span className="outreach-card-industry">{industryLabel(c.industry)}</span></div>
              <div className="outreach-card-meta">
                {c.email && <span>{c.email}</span>}
                {c.phone && <span> · {c.phone}</span>}
                {c.linkedin_url && <span> · LinkedIn</span>}
                <span> · prefers {CHANNEL_LABELS[c.preferred_channel] || "Email"}</span>
              </div>
              {c.relationship_note && <div className="outreach-card-note">"{c.relationship_note}"</div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
function MessagesTab({ messages, industries, industryLabel, loading, onSelect }) {
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const filtered = messages.filter((m) => {
    if (channel !== "all" && m.channel !== channel) return false;
    if (status !== "all" && m.status !== status) return false;
    return true;
  });
  return (
    <section data-testid="outreach-messages">
      <div className="outreach-toolbar">
        <select className="admin-select" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="outreach-messages-channel">
          <option value="all">All channels</option>
          {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="outreach-messages-status">
          <option value="all">All statuses</option>
          {MESSAGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading && <div className="outreach-empty">Loading…</div>}

      {!loading && messages.length === 0 && (
        <div className="outreach-empty" data-testid="outreach-messages-empty">
          <p className="outreach-empty-title">No messages yet.</p>
          <p className="outreach-empty-body">
            Head to <strong>People</strong>, click someone, and hit <strong>Draft message</strong>. The right industry angle gets slotted in automatically.
          </p>
        </div>
      )}

      {!loading && messages.length > 0 && filtered.length === 0 && (
        <div className="outreach-empty">No messages match those filters.</div>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="outreach-list" data-testid="outreach-messages-list">
          {filtered.map((m) => (
            <li key={m.id} className="outreach-card" onClick={() => onSelect(m)} data-testid="outreach-message-row">
              <div className="outreach-card-top">
                <div className="outreach-card-name">{m.contact_name} <span className="outreach-card-biz">· {m.contact_business}</span></div>
                <StatusBadge value={m.status} />
              </div>
              <div className="outreach-card-sub">
                <ChannelBadge value={m.channel} /> · {industryLabel(m.contact_industry)} · {fmtDate(m.updated_at || m.created_at)}
              </div>
              <div className="outreach-card-preview">{m.subject ? <strong>{m.subject}</strong> : null} {m.body?.slice(0, 160)}{m.body?.length > 160 ? "…" : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
function SendTab({ messages, stats, industryLabel, onSendEmail, onMarkSent, onCopy, onStatus, onSelect }) {
  const [channel, setChannel] = useState("all");
  const approved = messages.filter((m) => m.status === "approved" && (channel === "all" || m.channel === channel));
  const sent = messages.filter((m) => ["sent","replied","demo","won","declined"].includes(m.status) && (channel === "all" || m.channel === channel));
  const cap = stats?.send_cap || { today: 0, warn_at: 20, hard_at: 30 };
  const overWarn = cap.today >= cap.warn_at;
  const overHard = cap.today >= cap.hard_at;

  return (
    <section data-testid="outreach-send">
      <div className="outreach-cap" data-testid="outreach-send-cap">
        <div className="outreach-cap-label">
          Email sent today: <strong>{cap.today}</strong> / {cap.hard_at}
          {overHard ? <span className="outreach-cap-bad"> · daily cap reached</span> :
           overWarn ? <span className="outreach-cap-warn"> · take it easy — past {cap.warn_at} sends</span> :
           <span className="outreach-cap-ok"> · plenty of room</span>}
        </div>
        <div className="outreach-cap-bar">
          <div
            className={`outreach-cap-bar-fill ${overHard ? "is-bad" : overWarn ? "is-warn" : ""}`}
            style={{ width: `${Math.min(100, (cap.today / Math.max(1, cap.hard_at)) * 100)}%` }}
          />
        </div>
        <p className="outreach-cap-note">Personal 1:1 sends only. Texts: only people who know you / have opted in.</p>
      </div>

      <div className="outreach-toolbar">
        <select className="admin-select" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="outreach-send-channel">
          <option value="all">All channels</option>
          {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <h3 className="outreach-section-h">Ready to send ({approved.length})</h3>
      {approved.length === 0 ? (
        <div className="outreach-empty" data-testid="outreach-send-empty">
          <p className="outreach-empty-title">No approved messages yet.</p>
          <p className="outreach-empty-body">
            In <strong>Messages</strong>, open a draft and hit <strong>Approve</strong>. Approved messages show up here ready to send.
          </p>
        </div>
      ) : (
        <ul className="outreach-list" data-testid="outreach-send-list">
          {approved.map((m) => (
            <li key={m.id} className="outreach-card outreach-card-approved" data-testid="outreach-send-row">
              <div className="outreach-card-top">
                <div className="outreach-card-name" onClick={() => onSelect(m)}>{m.contact_name} <span className="outreach-card-biz">· {m.contact_business}</span></div>
                <ChannelBadge value={m.channel} />
              </div>
              <div className="outreach-card-sub">{industryLabel(m.contact_industry)}</div>
              <div className="outreach-card-preview">{m.subject ? <strong>{m.subject}</strong> : null} {m.body?.slice(0, 200)}{m.body?.length > 200 ? "…" : ""}</div>
              <div className="outreach-card-actions">
                {m.channel === "email" ? (
                  <button
                    type="button"
                    className="outreach-cta"
                    onClick={() => onSendEmail(m.id)}
                    disabled={overHard}
                    data-testid="outreach-send-email-btn"
                  >Send now</button>
                ) : (
                  <button type="button" className="outreach-cta" onClick={() => onMarkSent(m.id)} data-testid="outreach-mark-sent-btn">Mark as sent</button>
                )}
                <button type="button" className="outreach-cta-ghost" onClick={() => onCopy(m.body)} data-testid="outreach-copy-btn">Copy</button>
                <button type="button" className="outreach-cta-ghost" onClick={() => onSelect(m)} data-testid="outreach-edit-btn">Open</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h3 className="outreach-section-h" style={{ marginTop: 24 }}>Sent ({sent.length})</h3>
      {sent.length === 0 ? (
        <div className="outreach-empty-mini">Nothing sent yet.</div>
      ) : (
        <ul className="outreach-list" data-testid="outreach-sent-list">
          {sent.map((m) => (
            <li key={m.id} className="outreach-card" data-testid="outreach-sent-row">
              <div className="outreach-card-top">
                <div className="outreach-card-name" onClick={() => onSelect(m)}>{m.contact_name} <span className="outreach-card-biz">· {m.contact_business}</span></div>
                <StatusBadge value={m.status} />
              </div>
              <div className="outreach-card-sub">
                <ChannelBadge value={m.channel} /> · {industryLabel(m.contact_industry)} · sent {fmtDate(m.sent_at)}
              </div>
              <div className="outreach-card-actions">
                <select
                  className="admin-select"
                  value={m.status}
                  onChange={(e) => onStatus(m.id, e.target.value)}
                  data-testid="outreach-sent-status"
                >
                  {["sent", "replied", "demo", "won", "declined"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button type="button" className="outreach-cta-ghost" onClick={() => onSelect(m)}>Open</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ===========================================================================
function AddPersonModal({ industries, onCancel, onSave }) {
  const [f, setF] = useState({
    name: "", business: "", industry: industries[0]?.slug || "general-smb",
    email: "", phone: "", linkedin_url: "", relationship_note: "",
    preferred_channel: "email",
  });
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const payload = { ...f };
    if (!payload.email) delete payload.email;
    if (!payload.phone) delete payload.phone;
    if (!payload.linkedin_url) delete payload.linkedin_url;
    onSave(payload);
  };
  const ind = industries.find((i) => i.slug === f.industry);
  return (
    <ModalShell title="Add a person" onCancel={onCancel}>
      <form onSubmit={submit} className="outreach-form" data-testid="outreach-add-person-form">
        <div className="outreach-form-row">
          <Field label="Name" required>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} required data-testid="outreach-form-name" />
          </Field>
          <Field label="Business" required>
            <input value={f.business} onChange={(e) => set("business", e.target.value)} required data-testid="outreach-form-business" />
          </Field>
        </div>
        <Field label="Industry">
          <select value={f.industry} onChange={(e) => set("industry", e.target.value)} data-testid="outreach-form-industry">
            {industries.map((i) => (
              <option key={i.slug} value={i.slug}>
                {i.label}{!i.has_page ? " — no dedicated page yet" : ""}
              </option>
            ))}
          </select>
          {ind && !ind.has_page && (
            <p className="outreach-form-hint">Messages for this industry point at the homepage for now.</p>
          )}
        </Field>
        <div className="outreach-form-row">
          <Field label="Email">
            <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="outreach-form-email" />
          </Field>
          <Field label="Phone">
            <input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="outreach-form-phone" />
          </Field>
        </div>
        <Field label="LinkedIn URL">
          <input value={f.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="outreach-form-linkedin" />
        </Field>
        <Field label="How you know them">
          <textarea
            rows={3}
            value={f.relationship_note}
            onChange={(e) => set("relationship_note", e.target.value)}
            placeholder="e.g. 'we worked together at ProCloud' · 'you sorted my workshop fit-out last year' · 'we met at the Northside breakfast'"
            data-testid="outreach-form-note"
          />
        </Field>
        <Field label="Preferred channel">
          <select value={f.preferred_channel} onChange={(e) => set("preferred_channel", e.target.value)} data-testid="outreach-form-channel">
            {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <div className="outreach-modal-actions">
          <button type="button" className="outreach-cta-ghost" onClick={onCancel} data-testid="outreach-add-cancel">Cancel</button>
          <button type="submit" className="outreach-cta" data-testid="outreach-add-submit">Add person</button>
        </div>
      </form>
    </ModalShell>
  );
}

function PasteModal({ industries, onCancel, onSubmit }) {
  const [text, setText] = useState("");
  const [defaultIndustry, setDefaultIndustry] = useState("general-smb");
  const [defaultChannel, setDefaultChannel] = useState("email");
  const submit = (e) => {
    e.preventDefault();
    onSubmit(text, defaultIndustry, defaultChannel);
  };
  return (
    <ModalShell title="Paste a list" onCancel={onCancel}>
      <p className="outreach-modal-blurb">
        Paste either a <strong>CSV with a header row</strong> (<code>name,business,industry,email,phone,linkedin,note</code>),
        or one person per line in that same order. Industry must be one of the slugs below.
      </p>
      <form onSubmit={submit} className="outreach-form">
        <Field label="Paste here">
          <textarea
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"name,business,industry,email,phone,linkedin,note\nSam Briggs,Briggs Civil,civil,sam@briggs.example,,,old ProCloud client"}
            data-testid="outreach-paste-textarea"
          />
        </Field>
        <div className="outreach-form-row">
          <Field label="Default industry (when missing)">
            <select value={defaultIndustry} onChange={(e) => setDefaultIndustry(e.target.value)} data-testid="outreach-paste-default-industry">
              {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
            </select>
          </Field>
          <Field label="Default channel">
            <select value={defaultChannel} onChange={(e) => setDefaultChannel(e.target.value)} data-testid="outreach-paste-default-channel">
              {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
        </div>
        <div className="outreach-modal-actions">
          <button type="button" className="outreach-cta-ghost" onClick={onCancel} data-testid="outreach-paste-cancel">Cancel</button>
          <button type="submit" className="outreach-cta" data-testid="outreach-paste-submit">Import</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ContactDrawer({ contact, industries, industryLabel, onClose, onPatch, onDelete, onDraft, onRegenerate }) {
  const [c, setC] = useState(contact);
  useEffect(() => { setC(contact); }, [contact.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setC((cur) => ({ ...cur, [k]: v }));
  const save = () => onPatch({
    name: c.name, business: c.business, industry: c.industry,
    email: c.email || null, phone: c.phone || null,
    linkedin_url: c.linkedin_url || null,
    relationship_note: c.relationship_note,
    preferred_channel: c.preferred_channel, status: c.status,
  });
  const ind = industries.find((i) => i.slug === c.industry);
  return (
    <div className="admin-drawer-overlay" onClick={onClose} data-testid="outreach-contact-drawer">
      <aside className="admin-drawer" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-drawer-close" onClick={onClose} aria-label="Close" data-testid="outreach-contact-drawer-close">×</button>
        <div className="admin-drawer-head">
          <div className="admin-drawer-name">{c.name}</div>
          <div className="admin-drawer-biz">{c.business}</div>
          <div className="admin-drawer-meta">{industryLabel(c.industry)} · prefers {CHANNEL_LABELS[c.preferred_channel] || "Email"}</div>
        </div>

        <Field label="Name"><input value={c.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Business"><input value={c.business} onChange={(e) => set("business", e.target.value)} /></Field>
        <Field label="Industry">
          <select value={c.industry} onChange={(e) => set("industry", e.target.value)}>
            {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}{!i.has_page ? " — no dedicated page yet" : ""}</option>)}
          </select>
          {ind && !ind.has_page && <p className="outreach-form-hint">Messages will point at the homepage.</p>}
        </Field>
        <Field label="Email"><input value={c.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Phone"><input value={c.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="LinkedIn URL"><input value={c.linkedin_url || ""} onChange={(e) => set("linkedin_url", e.target.value)} /></Field>
        <Field label="How you know them">
          <textarea rows={3} value={c.relationship_note || ""} onChange={(e) => set("relationship_note", e.target.value)} />
        </Field>
        <Field label="Preferred channel">
          <select value={c.preferred_channel} onChange={(e) => set("preferred_channel", e.target.value)}>
            {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={c.status} onChange={(e) => set("status", e.target.value)}>
            {CONTACT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <div className="outreach-drawer-actions">
          <button type="button" className="outreach-cta" onClick={save} data-testid="outreach-contact-save">Save</button>
          <button type="button" className="outreach-cta-ghost" onClick={() => onDraft(c.preferred_channel)} data-testid="outreach-draft-btn">
            Draft message
          </button>
          <button type="button" className="outreach-cta-ghost" onClick={() => onRegenerate("email")} data-testid="outreach-draft-email">Draft email</button>
          <button type="button" className="outreach-cta-ghost" onClick={() => onRegenerate("linkedin")} data-testid="outreach-draft-linkedin">Draft LinkedIn</button>
          <button type="button" className="outreach-cta-ghost" onClick={() => onRegenerate("sms")} data-testid="outreach-draft-sms">Draft SMS</button>
          <button type="button" className="outreach-drawer-del" onClick={onDelete} data-testid="outreach-contact-delete">Remove</button>
        </div>
      </aside>
    </div>
  );
}

function MessageDrawer({ message, industryLabel, onClose, onSave, onApprove, onRegenerate, onSendEmail, onMarkSent, onDelete, onCopy, onStatus }) {
  const [subject, setSubject] = useState(message.subject || "");
  const [body, setBody] = useState(message.body || "");
  const [channel, setChannel] = useState(message.channel);
  const [auditLevel, setAuditLevel] = useState(message.audit_level || AUDIT_DEFAULT);
  useEffect(() => {
    setSubject(message.subject || "");
    setBody(message.body || "");
    setChannel(message.channel);
    setAuditLevel(message.audit_level || AUDIT_DEFAULT);
  }, [message.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = subject !== (message.subject || "") || body !== (message.body || "") || channel !== message.channel;
  const isApproved = message.status === "approved";
  const isSent = ["sent", "replied", "demo", "won", "declined"].includes(message.status);

  return (
    <div className="admin-drawer-overlay" onClick={onClose} data-testid="outreach-message-drawer">
      <aside className="admin-drawer outreach-message-drawer" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="admin-drawer-close" onClick={onClose} aria-label="Close" data-testid="outreach-message-drawer-close">×</button>
        <div className="admin-drawer-head">
          <div className="admin-drawer-name">{message.contact_name}</div>
          <div className="admin-drawer-biz">{message.contact_business}</div>
          <div className="admin-drawer-meta">
            <ChannelBadge value={channel} /> · {industryLabel(message.contact_industry)} · <StatusBadge value={message.status} />
            {message.audit_level && (
              <span className="outreach-audit-badge" data-testid="outreach-audit-current">
                Directness: <strong>{AUDIT_LABELS[message.audit_level] || message.audit_level}</strong>
              </span>
            )}
            {message.source === "template" && (
              <span className="outreach-audit-badge outreach-audit-badge-warn" data-testid="outreach-source-template">
                Template fallback
              </span>
            )}
          </div>
        </div>

        <div className="outreach-drawer-row">
          <Field label="Channel">
            <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="outreach-message-channel">
              {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Directness">
            <div className="outreach-segmented" role="radiogroup" aria-label="Directness" data-testid="outreach-audit-segmented">
              {AUDIT_LEVELS.map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  role="radio"
                  aria-checked={auditLevel === lvl}
                  className={`outreach-segmented-btn ${auditLevel === lvl ? "is-active" : ""}`}
                  onClick={() => setAuditLevel(lvl)}
                  data-testid={`outreach-audit-${lvl.toLowerCase()}`}
                >{AUDIT_LABELS[lvl]}</button>
              ))}
            </div>
            <p className="outreach-form-hint outreach-audit-hint">{AUDIT_HINTS[auditLevel]} Hit <strong>Regenerate</strong> to apply.</p>
          </Field>
        </div>

        {channel === "email" && (
          <Field label="Subject">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outreach-message-subject" />
          </Field>
        )}

        <Field label="Body">
          <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} data-testid="outreach-message-body" />
        </Field>

        {message.link && (
          <div className="outreach-message-link">
            Link inserted: <a href={message.link} target="_blank" rel="noreferrer">{message.link}</a>
          </div>
        )}

        <div className="outreach-drawer-actions">
          {dirty && (
            <button
              type="button"
              className="outreach-cta"
              onClick={() => onSave({ subject, body, channel })}
              data-testid="outreach-message-save"
            >Save changes</button>
          )}

          {!isApproved && !isSent && (
            <button type="button" className="outreach-cta-grad" onClick={onApprove} data-testid="outreach-message-approve">Approve</button>
          )}

          <button type="button" className="outreach-cta-ghost" onClick={() => onRegenerate(channel, auditLevel)} data-testid="outreach-message-regenerate">Regenerate</button>

          <button type="button" className="outreach-cta-ghost" onClick={onCopy} data-testid="outreach-message-copy">Copy</button>

          {isApproved && channel === "email" && (
            <button type="button" className="outreach-cta-grad" onClick={onSendEmail} data-testid="outreach-message-send">Send via Resend</button>
          )}
          {isApproved && channel !== "email" && (
            <button type="button" className="outreach-cta-grad" onClick={onMarkSent} data-testid="outreach-message-mark-sent">Mark as sent</button>
          )}

          {isSent && (
            <Field label="Status">
              <select value={message.status} onChange={(e) => onStatus(e.target.value)} data-testid="outreach-message-status">
                {["sent", "replied", "demo", "won", "declined"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}

          <button type="button" className="outreach-drawer-del" onClick={onDelete} data-testid="outreach-message-delete">Delete</button>
        </div>

        {message.sent_at && (
          <div className="outreach-message-meta">
            Sent {fmtDate(message.sent_at)}{message.resend_message_id ? ` · Resend id ${message.resend_message_id}` : ""}{message.sent_via === "mark_sent" ? " · marked manually" : ""}
          </div>
        )}
      </aside>
    </div>
  );
}

// shared modal shell
function ModalShell({ title, children, onCancel }) {
  return (
    <div className="admin-drawer-overlay" onClick={onCancel} data-testid="outreach-modal">
      <div className="outreach-modal" onClick={(e) => e.stopPropagation()}>
        <div className="outreach-modal-head">
          <h2 className="outreach-modal-title">{title}</h2>
          <button type="button" className="admin-drawer-close" onClick={onCancel} aria-label="Close" data-testid="outreach-modal-close">×</button>
        </div>
        <div className="outreach-modal-body">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="outreach-field">
      <span className="outreach-field-label">{label}{required && " *"}</span>
      {children}
    </label>
  );
}
